import { Menu, TextField, List, ListItem, ListItemText, Divider } from '@mui/material';
import { FC, useState, useEffect, MouseEvent, useCallback, memo } from 'react';

import { DRIVER_UNSET_LABEL } from 'src/constants/assignmentLabels';

export type SelectDriverMenuDriverEntity = {
  id: number;
  name: string;
  temporary: boolean;
}

type Props = {
  anchorEl: HTMLElement | null;
  onClose: (event: MouseEvent) => void;
  onDriverClick: (event: MouseEvent, id: number, name: string) => void;
  driverData: SelectDriverMenuDriverEntity[];
  driverIdsHasDelivery: number[];
  /** この行が現在割り当てているドライバー id（除外対象から外す）。未割当なら null。 */
  currentDriverId: number | null;
  isTemporaryDriver: boolean;
};

const SelectDriverMenuPresenter: FC<Props> = memo(({
  anchorEl,
  onClose,
  onDriverClick,
  driverData,
  driverIdsHasDelivery,
  currentDriverId,
  isTemporaryDriver,
}) => {
  const [searchDriverInputValue, setSearchDriverInputValue] = useState('');
  const [searchDriverQuery, setSearchDriverQuery] = useState<string>('');
  const [isComposing, setIsComposing] = useState(false);

  const handleDriverSearchQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setSearchDriverInputValue(event.target.value);
  }, []);

  useEffect(() => {
    if (isComposing) return;
    setSearchDriverQuery(searchDriverInputValue);
  }, [isComposing, searchDriverInputValue]);

  const filteredDrivers = driverData?.filter((it) => {
    if (it.temporary) return false;
    // 既に配車を持つ（稼働中の）ドライバーは選択肢から除外する（重複割当＝「勤務計画はすでに存在します」を防ぐ）。
    // ただし現在この行が割り当てているドライバーは除外しない（変更なしでも選べるように）。
    if (it.id !== currentDriverId && driverIdsHasDelivery.includes(it.id)) return false;
    return it.name.toLowerCase().includes(searchDriverQuery?.toLowerCase());
  }) ?? [];

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      sx={{ maxHeight: 500 }}
      slotProps={{
        paper: {
          sx: { padding: 0, margin: 0 }
        }
      }}
    >
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="検索"
        value={searchDriverInputValue}
        onChange={handleDriverSearchQueryChange}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
      />
      <List sx={{ p: 0, m: 0 }}>
        {!isTemporaryDriver && [
          <ListItem
            key={['it', -1].join('-')}
            component="div"
            onClick={(event) => onDriverClick(event, -1, DRIVER_UNSET_LABEL)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemText
              primary="未設定にする"
              slotProps={{
                primary: { sx: { fontSize: 14, color: 'black' } }
              }}
            />
          </ListItem>,
          <Divider key="unassign-divider" />,
        ]}
        {filteredDrivers.map((it) => (
          <ListItem
            key={['it', it.id].join('-')}
            component="div"
            onClick={(event) => onDriverClick(event, it.id, it.name)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemText
              primary={it.name}
              slotProps={{
                primary: { sx: { fontSize: 14, color: 'black' } },
              }}
            />
          </ListItem>
        ))}
      </List>
    </Menu>
  );
});

export default SelectDriverMenuPresenter;
